import { useState } from "react";
import { useBrandProfiles } from "@/api/useCatalogue";
import { cn } from "@/lib/cn";
import { CheckIcon } from "@/components/icons";

/**
 * Brand library — saved brand profiles. The currently-active profile gets
 * a tinted ring + an "Active" pill; clicking another card marks it active
 * (Phase 1 just mutates local state; Phase 2 persists via API).
 */
export function BrandLibraryPage() {
  const { data: brands = [], isLoading } = useBrandProfiles();
  const [activeId, setActiveId] = useState<string>("northshore");

  // Long logoText (e.g. "Northshore") overflows the fixed 72px box. Mirror
  // the kit previews (KitHero / BoxMockup3D): keep short marks as-is, but
  // fall back to brand initials so it always fits the square.
  function brandMark(logoText: string, name: string) {
    const text = (logoText || "").trim();
    if (text.length > 0 && text.length <= 4) return text;
    const initials = (name || text)
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 3)
      .toUpperCase();
    return initials || text.slice(0, 3).toUpperCase();
  }

  return (
    <div className="mx-auto max-w-7xl px-8 py-10">
      <header className="mb-8 flex items-end justify-between gap-5">
        <div>
          <div className="mb-2 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--studio)" }} />
            Brand library
          </div>
          <h1 className="m-0 mb-2 text-xl font-semibold leading-tight text-fg-1">
            Saved brand profiles
          </h1>
          <p className="m-0 max-w-2xl text-base leading-relaxed text-fg-3">
            Reusable brand kits — logo, palette and copy — applied to every kit you order.
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          style={{ background: "var(--studio)" }}
        >
          + New brand profile
        </button>
      </header>

      {isLoading ? (
        <div className="text-fg-3">Loading…</div>
      ) : (
        <ul data-chrome className="grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {brands.map((b) => {
            const active = b.id === activeId;
            return (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(b.id)}
                  className={cn(
                    "flex h-full w-full gap-3.5 rounded-lg border bg-white p-4 text-left transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
                    active
                      ? "border-[var(--studio)] shadow-[inset_0_0_0_1px_var(--studio)]"
                      : "border-[var(--border-1)] hover:border-[var(--studio)]",
                  )}
                >
                  <div className="grid h-[72px] w-[72px] flex-shrink-0 place-items-center overflow-hidden rounded-md border border-[var(--border-1)] bg-white p-1.5 text-center">
                    <span
                      className="max-w-full truncate text-lg font-extrabold leading-tight tracking-tight"
                      style={{ color: b.accent }}
                    >
                      {brandMark(b.logoText, b.name)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-sm font-semibold text-fg-1">{b.name}</p>
                    <p className="m-0 mt-0.5 text-xs text-fg-3">Updated {b.updated}</p>
                    <div className="mt-2 flex gap-0.5">
                      <span
                        className="h-4 w-4 rounded border border-black/5"
                        style={{ background: b.accent }}
                        aria-hidden
                      />
                      <span
                        className="h-4 w-4 rounded border border-black/5"
                        style={{ background: b.secondary }}
                        aria-hidden
                      />
                      <span
                        className="h-4 w-4 rounded border border-black/5 bg-white"
                        aria-hidden
                      />
                    </div>
                    {active && (
                      <span
                        className="mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider"
                        style={{ background: "var(--studio-tint)", color: "var(--studio-dark)" }}
                      >
                        <CheckIcon width={10} height={10} strokeWidth={2.5} /> Active
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
